'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UserProfile {
  name: string;
  email: string;
  avatar?: string;
}

const SettingsPanel = ({ isOpen, onClose }: SettingsPanelProps) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'preferences' | 'privacy'>('profile');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const mockUser: UserProfile = {
    name: 'Mindful User',
    email: 'user@thestillpoint.com',
  };

  const tabs = [
    { id: 'profile' as const, label: 'Profile', icon: 'UserCircleIcon' as const },
    { id: 'preferences' as const, label: 'Preferences', icon: 'Cog6ToothIcon' as const },
    { id: 'privacy' as const, label: 'Privacy', icon: 'ShieldCheckIcon' as const },
  ];

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-background z-600"
          onClick={handleBackdropClick}
        />
      )}

      <div
        className={`fixed bottom-0 left-0 right-0 lg:top-0 lg:right-0 lg:left-auto lg:bottom-auto h-[85vh] lg:h-full w-full lg:w-[420px] bg-card shadow-warm-lg z-700 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-y-0 lg:translate-x-0' : 'translate-y-full lg:translate-y-0 lg:translate-x-full'
        } rounded-t-xl lg:rounded-none`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <Icon name="Cog6ToothIcon" size={24} className="text-primary" />
              <h2 className="text-xl font-semibold text-foreground font-heading">Settings</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-md transition-therapeutic"
              aria-label="Close settings"
            >
              <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
            </button>
          </div>

          <div className="flex border-b border-border">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 p-4 transition-therapeutic ${
                  activeTab === tab.id
                    ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon} size={18} />
                <span className="text-sm font-medium font-caption">{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="UserCircleIcon" size={48} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground font-heading">
                      {mockUser.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">{mockUser.email}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2 font-caption">
                      Display Name
                    </label>
                    <input
                      type="text"
                      defaultValue={mockUser.name}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-0 transition-therapeutic"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2 font-caption">
                      Email Address
                    </label>
                    <input
                      type="email"
                      defaultValue={mockUser.email}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-0 transition-therapeutic"
                    />
                  </div>

                  <button className="w-full px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium transition-therapeutic shadow-warm hover:shadow-warm-md">
                    Update Profile
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Icon name="MoonIcon" size={20} className="text-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Dark Mode</p>
                        <p className="text-xs text-muted-foreground">Reduce eye strain</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsDarkMode(!isDarkMode)}
                      className={`relative w-12 h-6 rounded-full transition-therapeutic ${
                        isDarkMode ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-4 h-4 bg-card rounded-full transition-transform ${
                          isDarkMode ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Icon name="BellIcon" size={20} className="text-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Notifications</p>
                        <p className="text-xs text-muted-foreground">Daily reminders</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                      className={`relative w-12 h-6 rounded-full transition-therapeutic ${
                        notificationsEnabled ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-4 h-4 bg-card rounded-full transition-transform ${
                          notificationsEnabled ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Icon name="SpeakerWaveIcon" size={20} className="text-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Sound Effects</p>
                        <p className="text-xs text-muted-foreground">Message sounds</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSoundEnabled(!soundEnabled)}
                      className={`relative w-12 h-6 rounded-full transition-therapeutic ${
                        soundEnabled ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-4 h-4 bg-card rounded-full transition-transform ${
                          soundEnabled ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <button className="w-full flex items-center justify-between p-4 bg-muted/50 hover:bg-muted rounded-lg transition-therapeutic text-left">
                    <div className="flex items-center gap-3">
                      <Icon name="LockClosedIcon" size={20} className="text-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Change Password</p>
                        <p className="text-xs text-muted-foreground">Update your password</p>
                      </div>
                    </div>
                    <Icon name="ChevronRightIcon" size={16} className="text-muted-foreground" />
                  </button>

                  <button className="w-full flex items-center justify-between p-4 bg-muted/50 hover:bg-muted rounded-lg transition-therapeutic text-left">
                    <div className="flex items-center gap-3">
                      <Icon name="DocumentTextIcon" size={20} className="text-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Privacy Policy</p>
                        <p className="text-xs text-muted-foreground">View our privacy policy</p>
                      </div>
                    </div>
                    <Icon name="ChevronRightIcon" size={16} className="text-muted-foreground" />
                  </button>

                  <button className="w-full flex items-center justify-between p-4 bg-muted/50 hover:bg-muted rounded-lg transition-therapeutic text-left">
                    <div className="flex items-center gap-3">
                      <Icon name="TrashIcon" size={20} className="text-error" />
                      <div>
                        <p className="text-sm font-medium text-error">Delete Account</p>
                        <p className="text-xs text-muted-foreground">Permanently delete your data</p>
                      </div>
                    </div>
                    <Icon name="ChevronRightIcon" size={16} className="text-muted-foreground" />
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 border-t border-border">
            <button
              onClick={() => {}}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-error hover:bg-error/90 text-error-foreground rounded-lg font-medium transition-therapeutic shadow-warm hover:shadow-warm-md"
            >
              <Icon name="ArrowRightOnRectangleIcon" size={20} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPanel;